import {useMemo, useEffect, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {Column, Row} from 'react-table';
import ReactTooltip from 'react-tooltip';
import {useQuery} from 'react-query';
import _ from 'lodash';

import {
  TextFilter,
  ReactTable,
  MinMaxFilter,
} from '../../../core/components/react-table';
import {DicomocrResult} from '../../../models/evalon';
import {DicomInfo, getAllDicomSeries} from '../../../models/dicom-viewer';
import {fetchDICOMs, addLineBreaks} from '../../../models/dicom';
import {Badge} from '../../../core/components/badge';
import {useAxios} from 'src/utils/http';

export const DicomocrResultsTable = ({
  dicomocrResults,
  datasetDicomInfo,
  datasetDicomInfoIsError,
  datasetID,
  selected = [],
  onSelect,
  isBulkReview,
}: {
  dicomocrResults: DicomocrResult[];
  datasetDicomInfo?: DicomInfo;
  datasetDicomInfoIsError?: boolean;
  datasetID: number;
  selected: string[];
  onSelect: (sopIds: string[], checked: boolean) => void;
  isBulkReview?: boolean;
}) => {
  const api = useAxios();
  const [pageSOPIDs, pageSOPIDsChange] = useState<string[]>([]);
  const location = useLocation();

  // Determine if we're on the bulk review page if prop is not explicitly provided
  const isBulkReviewPage =
    isBulkReview !== undefined
      ? isBulkReview
      : location.pathname.includes('/admin/datasets/review-bulk');

  const allDicomSeries = useMemo(
    () => getAllDicomSeries(datasetDicomInfo),
    [datasetDicomInfo]
  );
  const datasetDicomInfoIsErrorMemo = useMemo(
    () => datasetDicomInfoIsError,
    [datasetDicomInfoIsError]
  );

  const {data: dicoms, isLoading: dicomsIsLoading} = useQuery(
    [datasetID, 'dicomocrReview', pageSOPIDs],
    () => {
      return fetchDICOMs(api, pageSOPIDs);
    },
    {
      enabled: !_.isNil(datasetID) && !_.isEmpty(pageSOPIDs),
      keepPreviousData: true,
      staleTime: 60 * 60 * 1000, // 1 hour
    }
  );

  const columns = useMemo<Array<Column<DicomocrResult>>>(() => {
    const selectedCol: Column<DicomocrResult> = {
      id: 'selected',
      width: 70,
      Header: props => {
        const {page} = props;

        const selectedState = _.every(page, row =>
          _.includes(selected, row.original.sopID)
        )
          ? 'all'
          : _.some(page, row => _.includes(selected, row.original.sopID))
          ? 'indeterminate'
          : 'none';

        return (
          <div className="inline">
            <input
              type="checkbox"
              className="checkbox-input"
              checked={_.includes(['all', 'indeterminate'], selectedState)}
              ref={el =>
                el && (el.indeterminate = selectedState === 'indeterminate')
              }
              onChange={e =>
                onSelect(
                  _.map(page, row => row.original.sopID ?? ''),
                  e.target.checked
                )
              }
            />
          </div>
        );
      },
      accessor: row => {
        return _.includes(selected, row.sopID);
      },
      Cell: ({row}: {value: boolean; row: Row<DicomocrResult>}) => {
        const sopID = row.original.sopID ?? '';
        const included = _.includes(selected, sopID);
        return (
          <>
            <input
              type="checkbox"
              className="checkbox-input"
              checked={included}
              onChange={e => onSelect([sopID], e.target.checked)}
            />
          </>
        );
      },
      disableSortBy: !isBulkReviewPage,
      disableFilters: !isBulkReviewPage,
    };

    const blackoutStatusCol: Column<DicomocrResult> = {
      id: 'blackout_status',
      width: 140,
      Header: 'Blackout Status',
      accessor: () => true,
      Cell: ({row}: {value: boolean; row: Row<DicomocrResult>}) => {
        const status = row.original.blackoutStatus ?? '';
        if (status === '') {
          return <Badge type={'info'}>Unsaved</Badge>;
        }
        return <Badge type={'success'}>{status}</Badge>;
      },
      disableSortBy: true,
      disableFilters: true,
    };

    const statusCol: Column<DicomocrResult> = {
      id: 'status',
      width: 140,
      Header: 'Status',
      accessor: () => true,
      Cell: ({row}: {value: boolean; row: Row<DicomocrResult>}) => {
        const sopID = row.original.sopID ?? '';
        const image = dicoms?.find(dicom => dicom.sopID === sopID);
        if (dicomsIsLoading) {
          return <Badge type={'info'}>Loading...</Badge>;
        } else if (_.isNil(image)) {
          return <Badge type={'warning'}>Error Loading</Badge>;
        } else if (image.phiHas) {
          return <Badge type={'danger'}>PHI</Badge>;
        } else {
          return image.phiNeedsReview ? (
            <Badge type={'info'}>Not Reviewed</Badge>
          ) : (
            <Badge type={'success'}>Reviewed</Badge>
          );
        }
      },
      disableSortBy: true,
      disableFilters: true,
    };

    const charCountBulkCol: Column<DicomocrResult> = {
      Header: '# of Characters',
      id: 'charCount',
      width: 120,
      accessor: (row: DicomocrResult) => {
        const detextedTextLengths = row.detectedText
          .split('|')
          .map((text: string) => text.trim().length);
        return `${_.min(detextedTextLengths) || 0} - ${
          _.max(detextedTextLengths) || 0
        }`;
      },
      Filter: MinMaxFilter,
      filter: (rows: any[], _columnIds: string[], filterValue: number[] = []) =>
        _.filter(rows, (row: any) => {
          const detextedTextLengths = row.original.detectedText
            ?.split('|')
            .map((text: string) => text.trim().length);
          const minLength = (_.min(detextedTextLengths) as number) || 0;
          const maxLength = (_.max(detextedTextLengths) as number) || 0;

          // min filter
          if (!_.isNil(filterValue[0])) {
            if (maxLength < filterValue[0]) {
              return false;
            }
          }

          // max filter
          if (!_.isNil(filterValue[1])) {
            if (minLength > filterValue[1]) {
              return false;
            }
          }
          return true;
        }),
      disableSortBy: false,
      sortType: (rowA: any, rowB: any, _id: string, desc?: boolean) => {
        const detextedTextLengthsA = rowA.original.detectedText
          ?.split('|')
          .map((text: string) => text.trim().length);
        const detextedTextLengthsB = rowB.original.detectedText
          ?.split('|')
          .map((text: string) => text.trim().length);

        let a, b: number;
        if (!desc) {
          a =
            (_.min(detextedTextLengthsA) as number) || Number.MAX_SAFE_INTEGER;
          b =
            (_.min(detextedTextLengthsB) as number) || Number.MAX_SAFE_INTEGER;
        } else {
          a =
            (_.max(detextedTextLengthsA) as number) || -Number.MAX_SAFE_INTEGER;
          b =
            (_.max(detextedTextLengthsB) as number) || -Number.MAX_SAFE_INTEGER;
        }

        if (a > b) {
          return 1;
        }
        if (b > a) {
          return -1;
        }

        return 0;
      },
    };

    const phiCharCountBulkCol: Column<DicomocrResult> = {
      Header: 'PHI Len',
      id: 'phiCharCount',
      width: 120,
      accessor: (row: DicomocrResult) => {
        const detextedTextLengths = row.detectedTextPHI
          .split('|')
          .map((text: string) => text.trim().length);
        return `${_.min(detextedTextLengths) || 0} - ${
          _.max(detextedTextLengths) || 0
        }`;
      },
      Filter: MinMaxFilter,
      filter: (rows: any[], _columnIds: string[], filterValue: number[] = []) =>
        _.filter(rows, (row: any) => {
          const detextedTextLengths = row.original.detectedTextPHI
            ?.split('|')
            .map((text: string) => text.trim().length);
          const minLength = (_.min(detextedTextLengths) as number) || 0;
          const maxLength = (_.max(detextedTextLengths) as number) || 0;

          // min filter
          if (!_.isNil(filterValue[0])) {
            if (maxLength < filterValue[0]) {
              return false;
            }
          }

          // max filter
          if (!_.isNil(filterValue[1])) {
            if (minLength > filterValue[1]) {
              return false;
            }
          }
          return true;
        }),
      disableSortBy: false,
      sortType: (rowA: any, rowB: any, _id: string, desc?: boolean) => {
        const detextedTextLengthsA = rowA.original.detectedTextPHI
          ?.split('|')
          .map((text: string) => text.trim().length);
        const detextedTextLengthsB = rowB.original.detectedTextPHI
          ?.split('|')
          .map((text: string) => text.trim().length);

        let a, b: number;
        if (!desc) {
          a =
            (_.min(detextedTextLengthsA) as number) || Number.MAX_SAFE_INTEGER;
          b =
            (_.min(detextedTextLengthsB) as number) || Number.MAX_SAFE_INTEGER;
        } else {
          a =
            (_.max(detextedTextLengthsA) as number) || -Number.MAX_SAFE_INTEGER;
          b =
            (_.max(detextedTextLengthsB) as number) || -Number.MAX_SAFE_INTEGER;
        }

        if (a > b) {
          return 1;
        }
        if (b > a) {
          return -1;
        }

        return 0;
      },
    };

    const charCountRegularCol: Column<DicomocrResult> = {
      Header: '# of Characters',
      id: 'charCount',
      width: 120,
      accessor: (row: DicomocrResult) => row.detectedText.length,
      Filter: MinMaxFilter,
      filter: 'between',
      disableSortBy: false,
    };

    const detectedTextPHICol: Column<DicomocrResult> = {
      Header: 'Detected Text - PHI',
      id: 'detectedTextPHI',
      accessor: 'detectedTextPHI',
      width: 500,
      Filter: TextFilter,
      filter: 'inclues',
      Cell: ({row}: {row: Row<DicomocrResult>}) => {
        const text = row.original.detectedTextPHI;
        return <pre>{addLineBreaks(text)}</pre>;
      },
    };

    const detectedTextCol: Column<DicomocrResult> = {
      Header: 'Detected Text',
      id: 'detectedText',
      accessor: 'detectedText',
      width: 700,
      Filter: TextFilter,
      filter: 'inclues',
      Cell: ({row}: {row: Row<DicomocrResult>}) => {
        const text = row.original.detectedText;
        return <pre>{addLineBreaks(text)}</pre>;
      },
    };

    const maxConfidenceCol: Column<DicomocrResult> = {
      Header: 'Max Confidence',
      accessor: 'phiMaxConfidence',
      width: 100,
      Filter: TextFilter,
      filter: 'inclues',
    };

    const studyIDCol: Column<DicomocrResult> = {
      Header: 'Study ID',
      width: 550,
      accessor: 'studyID',
      Filter: TextFilter,
      filter: 'inclues',
    };

    const seriesIDCol: Column<DicomocrResult> = {
      Header: 'Series ID',
      width: 550,
      accessor: (row: DicomocrResult) =>
        _.find(dicoms, {sopID: row.sopID})?.seriesID,
      Filter: TextFilter,
      filter: 'includes',
    };

    const sopIDCol: Column<DicomocrResult> = {
      Header: 'SOP ID',
      width: 550,
      accessor: 'sopID',
      Filter: TextFilter,
      filter: 'inclues',
    };

    const viewDicomsCol: Column<DicomocrResult> = {
      id: 'view',
      width: 100,
      Header: 'View DICOMs',
      accessor: (row: DicomocrResult) => {
        return _.includes(selected, row.sopID);
      },
      Cell: ({row}: {value: boolean; row: Row<DicomocrResult>}) => {
        if (datasetDicomInfoIsErrorMemo) {
          return <Badge type="danger">Error</Badge>;
        } else if (allDicomSeries === undefined) {
          return 'Loading..';
        }

        const studyID = row.original.studyID;
        const sopID = row.original.sopID;

        const dicom = dicoms?.find(d => d.sopID === sopID);
        const seriesID = dicom?.seriesID;
        const dicomsCanBeViewed = !_.isNil(dicom);

        return (
          <Link
            to={`/admin/datasets/review/${datasetID}/dicomViewer/${studyID}/${seriesID}/${sopID}`}
          >
            <span
              data-tip={
                !dicomsCanBeViewed
                  ? 'DICOMs are not available to view for this Sop ID.'
                  : undefined
              }
            >
              <button
                className="btn btn-xs btn-primary -my-2"
                disabled={!dicomsCanBeViewed}
              >
                View DICOM
              </button>
            </span>
          </Link>
        );
      },
      disableSortBy: true,
      disableFilters: true,
    };

    // Compose columns based on mode
    const tableColumns = isBulkReviewPage
      ? [
          selectedCol,
          statusCol,
          // Dicomocr Result section first in bulk review
          {
            Header: 'Dicomocr Result',
            columns: [
              phiCharCountBulkCol,
              charCountBulkCol,
              detectedTextPHICol,
              detectedTextCol,
            ],
          },
          // Study Info section second in bulk review
          {
            Header: 'Study Info',
            columns: [studyIDCol, viewDicomsCol],
          },
        ]
      : [
          selectedCol,
          blackoutStatusCol,
          statusCol,
          // Study Info section first in regular mode
          {
            Header: 'Study Info',
            columns: [studyIDCol, seriesIDCol, sopIDCol, viewDicomsCol],
          },
          // Dicomocr Result section second in regular mode
          {
            Header: 'Dicomocr Result',
            columns: [
              charCountRegularCol,
              detectedTextPHICol,
              detectedTextCol,
              maxConfidenceCol,
            ],
          },
        ];

    return tableColumns;
  }, [
    selected,
    onSelect,
    dicoms,
    dicomsIsLoading,
    datasetID,
    datasetDicomInfoIsErrorMemo,
    allDicomSeries,
    isBulkReviewPage,
    pageSOPIDs,
  ]);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [columns]);

  const data = useMemo(() => dicomocrResults, [dicomocrResults]);

  const renderShowCount = (
    pageSize: number,
    pageIndex: number,
    rowsLength: number
  ) => {
    return (
      <div className="mt-4 mb-2 text-xl flex items-center">
        DICOMocr Results{' '}
        <div className="ml-2 text-gray-500 text-base">
          (Showing {(pageSize * pageIndex + 1).toLocaleString('en-US')}-
          {Math.min(pageSize * (pageIndex + 1), rowsLength).toLocaleString(
            'en-US'
          )}{' '}
          of {rowsLength.toLocaleString('en-US')} DICOMs)
        </div>
      </div>
    );
  };

  return dicomocrResults.length > 0 ? (
    <ReactTable
      tableColumns={columns}
      tableData={data}
      renderCustomShownCount={renderShowCount}
      defaultSortBy={[
        {id: 'evalName', desc: false},
        {id: 'detectedText', desc: true},
      ]}
      onPageChange={page => {
        const newPageSOPIDs = page.map(row => row.original.sopID);
        if (!_.isEqual(pageSOPIDs, newPageSOPIDs))
          pageSOPIDsChange(newPageSOPIDs);
      }}
      enableResize={true}
    />
  ) : (
    <div className="text-xl mt-4 mb-2">No Dicomocr Results</div>
  );
};
